import { useState, useEffect } from 'react';
import { ChevronIcon } from '../../icons/ButtonIcons';
import { useNetworkElementsStore } from '../../../store/networkElementsStore';
import { useStressTestStore } from '../../../store/stressTestStore';
import { useViewMode } from '../../../context/useViewMode';
import { SmartMeterGateway, TechnischeRessource, isHausanschluss, isTrafoStation } from '../../../types/network';
import { fmt } from '../../../utils/numberFormat';
import { buildMeterRows } from '../../../utils/meterRows';
import { archiveProvenance, houseProvenance } from '../../../utils/meterProvenance';
import { useHistoricalPlaybackStore } from '../../../store/historicalPlaybackStore';
import { ProvenanceBadge } from './meters/MeterBadges';
import { LOADING_COMPLIANCE_LIMIT_PCT, LOADING_WARNING_PCT } from '../../../utils/loadingConstants';

/** Aggregated SMGW measurement values */
interface SmgwAggregated {
  totalPowerKw: number;
  totalCurrentA: number;
  activePowerL1W: number;
  activePowerL2W: number;
  activePowerL3W: number;
  voltageL1V: number | null;
  voltageL2V: number | null;
  voltageL3V: number | null;
  currentL1A: number;
  currentL2A: number;
  currentL3A: number;
}

/** Aggregate all SMGWs with hasRealtimeData into a single measurement set */
function aggregateSmgwData(smgws: SmartMeterGateway[]): SmgwAggregated | null {
  const active = smgws.filter(s => s.hasRealtimeData && s.currentPowerW != null);
  if (active.length === 0) return null;

  let totalPowerW = 0;
  let pL1 = 0, pL2 = 0, pL3 = 0;
  let cL1 = 0, cL2 = 0, cL3 = 0;
  let vL1Sum = 0, vL2Sum = 0, vL3Sum = 0;
  let vL1Count = 0, vL2Count = 0, vL3Count = 0;

  for (const s of active) {
    totalPowerW += s.currentPowerW ?? 0;
    cL1 += s.currentL1A ?? 0;
    cL2 += s.currentL2A ?? 0;
    cL3 += s.currentL3A ?? 0;
    pL1 += s.activePowerL1W ?? 0;
    pL2 += s.activePowerL2W ?? 0;
    pL3 += s.activePowerL3W ?? 0;
    if (s.voltageL1V != null) { vL1Sum += s.voltageL1V; vL1Count++; }
    if (s.voltageL2V != null) { vL2Sum += s.voltageL2V; vL2Count++; }
    if (s.voltageL3V != null) { vL3Sum += s.voltageL3V; vL3Count++; }
  }

  const totalCurrentA = cL1 + cL2 + cL3;

  return {
    totalPowerKw: totalPowerW / 1000,
    totalCurrentA,
    activePowerL1W: pL1,
    activePowerL2W: pL2,
    activePowerL3W: pL3,
    voltageL1V: vL1Count > 0 ? vL1Sum / vL1Count : null,
    voltageL2V: vL2Count > 0 ? vL2Sum / vL2Count : null,
    voltageL3V: vL3Count > 0 ? vL3Sum / vL3Count : null,
    currentL1A: cL1,
    currentL2A: cL2,
    currentL3A: cL3,
  };
}

interface PowerMeasurementsSectionProps {
  elementId: string;
  hasPower?: boolean;
  technicalResources?: TechnischeRessource[];
  isGridInfrastructure?: boolean;
  hidePhaseValues?: boolean;
  hideDetailedMeasurements?: boolean;
  smgws?: SmartMeterGateway[];
  /**
   * Anschlussleistung of the Hausanschluss (kW). Used to derive the loading
   * from the current when the load-flow result carries none (archive
   * snapshots) — I_n = P_conn / (√3 · 400 V), the fuse rating the
   * Anschlussleistung was derived from.
   */
  connectionPowerKw?: number;
}

export const PowerMeasurementsSection: React.FC<PowerMeasurementsSectionProps> = ({
  elementId,
  hidePhaseValues = false,
  hideDetailedMeasurements = false,
  smgws,
  connectionPowerKw,
}) => {
  // Move all hooks to the top
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [, setLastMeasurement] = useState<number | null>(null);
  const [, setTimeAgoTick] = useState(0);

  // Check view mode
  const viewMode = useViewMode();
  const isStressTestMode = viewMode === 'stress-test';

  // Live data from powerMeasurements store
  const powerMeasurements = useNetworkElementsStore(state => state.powerMeasurements);

  // Stress test data from stressTestStore
  const stressTestResults = useStressTestStore(state => state.stressTestResults);
  const stressData = stressTestResults?.results?.[elementId];

  // Get the element
  const element = useNetworkElementsStore(state => state.getElement(elementId));
  const rawMeasurements = element ? powerMeasurements[element.id] : null;

  // Historical playback: the store holds the archived frame; its meter inputs say what was measured
  const playbackMode = useHistoricalPlaybackStore(state => state.mode);
  const playbackTs = useHistoricalPlaybackStore(state => state.effectiveTs);
  // Same rule as the meter card: a rewound frame once its instant is known; a check frame is neither live nor archive
  const isHistorical = playbackMode === 'historical' && playbackTs != null;
  const isCheckFrame = playbackMode !== 'live' && !isHistorical;
  const snapshotHasMeterValues = useHistoricalPlaybackStore(state => state.meterValuesByElement !== null);
  const archivedInputs = useHistoricalPlaybackStore(state => state.meterValuesByElement?.[elementId]);

  // A Hausanschluss shows its LOAD-FLOW value (the number map, grid and compliance use) with its origin;
  // the SMGW live sum is only the fallback without a load-flow result (and never in a rewound frame).
  // Per-meter measured values live in the "Zähler" card.
  const isHouse = element != null && isHausanschluss(element);
  const smgwData = smgws && smgws.length > 0 ? aggregateSmgwData(smgws) : null;
  const hasSmgwData = smgwData !== null && !isStressTestMode && !(isHouse && rawMeasurements) && playbackMode === 'live';

  // Determine power value based on mode
  let totalPower: number;
  if (isStressTestMode) {
    // Stress test mode - ONLY use stress test data, never fall back to live
    totalPower = stressData?.powerKw ?? 0;
  } else if (hasSmgwData) {
    // SMGW data available - prefer real measurements
    totalPower = smgwData.totalPowerKw;
  } else if (rawMeasurements && element) {
    // Live mode - use power measurements
    totalPower = isTrafoStation(element)
      ? rawMeasurements.resultValue || rawMeasurements.totalActivePower || 0
      : rawMeasurements.totalActivePower || 0;
  } else {
    totalPower = 0;
  }


  // Update lastUpdate when we get new measurements
  useEffect(() => {
    if (rawMeasurements?.resultValue === null) {
      setLastMeasurement(rawMeasurements.totalActivePower);
      setLastUpdate(rawMeasurements.timestamp || Date.now());
    }
  }, [rawMeasurements]);

  // Update the "time ago" display every second
  useEffect(() => {
    const timer = setInterval(() => {
      // Force re-render to update time display (setLastUpdate(prev => prev) would bail out)
      setTimeAgoTick(t => t + 1);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Early returns after all hooks
  if (!element) {
    return null;
  }

  // Only show power measurements for hausanschluss and trafostation
  if (!isHausanschluss(element) && !isTrafoStation(element)) {
    return null;
  }

  // A house without load-flow result and without live SMGW sum has no value — say so instead of "0,00 kW"
  const noHouseValue = isHouse && !isStressTestMode && !rawMeasurements && !hasSmgwData;

  // Origin of the house value next to it — every power figure says whether it is measured (TAF 10) or estimated
  const provenance = !isHausanschluss(element) || isStressTestMode || noHouseValue
    ? null
    : isCheckFrame
      ? { kind: 'unknown' as const, label: 'berechneter Fall', description: 'Die Karte zeigt einen berechneten Fall (z. B. Netzanschlussprüfung) — der Wert ist weder Messung noch Live-Schätzung.' }
      : isHistorical
      ? archiveProvenance(snapshotHasMeterValues ? archivedInputs ?? [] : undefined)
      : rawMeasurements
        ? houseProvenance(rawMeasurements.inputSource, buildMeterRows(element.smgws ?? [], element.melos ?? [], element.meloCurrentPool))
        : hasSmgwData
          ? { kind: 'measured' as const, label: 'gemessen · TAF 10', description: 'Summe der TAF-10-Live-Werte der Smart Meter Gateways — für diesen Anschluss liegt noch kein Lastfluss-Ergebnis vor.' }
          : null;

  // Age of the shown values. Recent values read as "vor n Min."; older ones
  // (typically a rewound archive instant) as a full German date without seconds.
  const getTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `vor ${seconds} s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `vor ${minutes} Min.`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    return `${new Date(timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr`;
  };

  // Determine current and loading values based on mode
  let currentValue: number;
  let loadingValue: number | null;

  if (isStressTestMode) {
    // Stress test mode - ONLY use stress test data, never fall back to live
    currentValue = stressData?.currentA ?? 0;
    loadingValue = stressData?.loadingPercent ?? null;
  } else if (hasSmgwData) {
    // SMGW data - use measured current, backend-provided loading
    currentValue = smgwData.totalCurrentA;
    loadingValue = rawMeasurements?.loadingPercent ?? null;
  } else {
    // Live mode - use backend-provided current and loading (single source of truth)
    const phases = rawMeasurements?.phases;
    currentValue = phases
      ? (phases.L1?.current ?? 0) + (phases.L2?.current ?? 0) + (phases.L3?.current ?? 0)
      : 0;
    loadingValue = rawMeasurements?.loadingPercent ?? null;
  }

  // No loading from the result (archive snapshots don't carry one for houses):
  // derive it from the current against the connection's rated current instead
  // of showing a misleading "0,0 %" next to a real current.
  let loadingDerived = false;
  if ((loadingValue == null || loadingValue === 0) && currentValue > 0 && connectionPowerKw && connectionPowerKw > 0) {
    const ratedCurrentA = (connectionPowerKw * 1000) / (Math.sqrt(3) * 400);
    // Sum of the three phase currents ↔ 3 × I_n
    loadingValue = (currentValue / (3 * ratedCurrentA)) * 100;
    loadingDerived = true;
  }

  // Loading color
  const getLoadColor = (pct: number) => {
    if (pct > LOADING_COMPLIANCE_LIMIT_PCT) return 'text-red-600 dark:text-red-400';
    if (pct > LOADING_WARNING_PCT) return 'text-orange-600 dark:text-orange-400';
    return 'text-gray-900 dark:text-white';
  };

  const accentColor = isStressTestMode ? 'bg-orange-400 dark:bg-orange-500' : 'bg-amber-400 dark:bg-amber-500';
  const iconBg = isStressTestMode ? 'bg-orange-100 dark:bg-orange-900/40' : 'bg-amber-100 dark:bg-amber-900/40';
  const iconColor = isStressTestMode ? 'text-orange-600 dark:text-orange-400' : 'text-amber-600 dark:text-amber-400';

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentColor}`} />
      <div className="pl-4 pr-3 py-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <div className={`shrink-0 w-6 h-6 rounded-full ${iconBg} flex items-center justify-center`}>
              <svg className={`w-3.5 h-3.5 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {isStressTestMode ? 'Stresstest' : isHouse ? 'Leistung am Anschluss' : 'Leistungsmessungen'}
            </span>
            {provenance && <ProvenanceBadge provenance={provenance} />}
          </div>
          {!isStressTestMode && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500 shrink-0 whitespace-nowrap">{getTimeAgo(lastUpdate)}</span>
          )}
          {isStressTestMode && (
            <span className="text-[10px] bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded font-medium">
              Stresstest
            </span>
          )}
        </div>

        {/* Value-first metrics */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-2.5 py-2">
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {noHouseValue ? '–' : fmt(totalPower, 2)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kW</span>
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400" title={isHouse && !isStressTestMode ? (hasSmgwData ? 'Summe der TAF-10-Live-Werte (noch kein Lastfluss-Ergebnis)' : 'Wert aus dem Lastfluss — dieselbe Zahl wie Karte und Netzliste. Die Zählerliste unten zeigt die einzelnen Zählerwerte; sie können zeitlich leicht abweichen.') : undefined}>{isHouse && !isStressTestMode ? (noHouseValue ? 'kein Lastfluss-Ergebnis' : hasSmgwData ? 'Netto · TAF 10' : 'Netto · Lastfluss') : 'Gesamt'}</div>
          </div>
          <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-2.5 py-2">
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              {noHouseValue ? '–' : fmt(currentValue, 1)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">A</span>
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Strom</div>
          </div>
          <div
            className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-2.5 py-2"
            title={loadingDerived
              ? `Aus dem Strom und der Anschlussleistung (${fmt(connectionPowerKw ?? 0, 1)} kW ≙ ${fmt((connectionPowerKw ?? 0) * 1000 / (Math.sqrt(3) * 400), 0)} A je Phase) abgeleitet.`
              : loadingValue == null ? 'Keine Auslastung im Berechnungsergebnis.' : 'Auslastung des Hausanschlusses laut Lastfluss.'}
          >
            <div className={`text-lg font-bold ${loadingValue == null ? 'text-gray-400 dark:text-primary-400' : getLoadColor(loadingValue)}`}>
              {loadingValue == null ? '–' : fmt(loadingValue, 1)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">%</span>
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400">Auslastung{loadingDerived ? ' (abgeleitet)' : ''}</div>
          </div>
        </div>

        {/* Phase power - compact row */}
        {!hidePhaseValues && hasSmgwData && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {(['L1', 'L2', 'L3'] as const).map((phase, i) => {
              const powerW = [smgwData.activePowerL1W, smgwData.activePowerL2W, smgwData.activePowerL3W][i];
              return (
                <div key={phase} className="text-center bg-gray-50 dark:bg-primary-700/50 rounded-md py-1.5 px-1">
                  <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500">{phase}</p>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {fmt((powerW / 1000), 2)} kW
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {!hidePhaseValues && !hasSmgwData && rawMeasurements?.phases && rawMeasurements.resultValue === null && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {Object.entries(rawMeasurements.phases).map(([phase, values]) => (
              <div key={phase} className="text-center bg-gray-50 dark:bg-primary-700/50 rounded-md py-1.5 px-1">
                <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500">{phase}</p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {typeof values === 'object' && values && 'power' in values ? fmt(values.power, 2) : '0.00'} kW
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Detailed measurements - collapsible */}
        {!hideDetailedMeasurements && (hasSmgwData || (rawMeasurements?.phases && rawMeasurements.resultValue === null)) && (
          <div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-between py-1.5 text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <span className="font-medium">Detaillierte Messungen</span>
              <ChevronIcon direction={isExpanded ? "up" : "down"} className="w-3.5 h-3.5" />
            </button>

            {isExpanded && hasSmgwData && (
              <div className="space-y-1 mt-1">
                {(['L1', 'L2', 'L3'] as const).map((phase, i) => {
                  const voltage = [smgwData.voltageL1V, smgwData.voltageL2V, smgwData.voltageL3V][i];
                  const current = [smgwData.currentL1A, smgwData.currentL2A, smgwData.currentL3A][i];
                  const powerW = [smgwData.activePowerL1W, smgwData.activePowerL2W, smgwData.activePowerL3W][i];
                  return (
                    <div key={phase} className="grid grid-cols-4 gap-2 text-xs py-1.5 px-2 bg-gray-50 dark:bg-primary-700/50 rounded">
                      <span className={`font-semibold ${
                        phase === 'L1' ? 'text-red-500 dark:text-red-400' :
                        phase === 'L2' ? 'text-yellow-500 dark:text-yellow-400' :
                        'text-blue-500 dark:text-blue-400'
                      }`}>{phase}</span>
                      <span className="text-right text-gray-600 dark:text-gray-300">{voltage !== null ? `${fmt(voltage, 1)} V` : '\u2013'}</span>
                      <span className="text-right text-gray-600 dark:text-gray-300">{fmt(current, 1)} A</span>
                      <span className="text-right text-gray-600 dark:text-gray-300">{fmt((powerW / 1000), 2)} kW</span>
                    </div>
                  );
                })}
              </div>
            )}

            {isExpanded && !hasSmgwData && rawMeasurements?.phases && rawMeasurements.resultValue === null && (
              <div className="space-y-1 mt-1">
                {Object.entries(rawMeasurements.phases).map(([phase, values]) => (
                  <div key={phase} className="grid grid-cols-4 gap-2 text-xs py-1.5 px-2 bg-gray-50 dark:bg-primary-700/50 rounded">
                    <span className={`font-semibold ${
                      phase === 'L1' ? 'text-red-500 dark:text-red-400' :
                      phase === 'L2' ? 'text-yellow-500 dark:text-yellow-400' :
                      'text-blue-500 dark:text-blue-400'
                    }`}>{phase}</span>
                    <span className="text-right text-gray-600 dark:text-gray-300">
                      {typeof values === 'object' && values && 'voltage' in values ? `${fmt(values.voltage, 1)} V` : '0.0 V'}
                    </span>
                    <span className="text-right text-gray-600 dark:text-gray-300">
                      {typeof values === 'object' && values && 'current' in values ? `${fmt(values.current, 1)} A` : '0.0 A'}
                    </span>
                    <span className="text-right text-gray-600 dark:text-gray-300">
                      {typeof values === 'object' && values && 'power' in values ? `${fmt(values.power, 2)} kW` : '0.00 kW'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
